import * as React from 'react';
import { BookOpen, Check, GitBranch, Pencil, ShieldCheck } from 'lucide-react';
import {
  VendorCitation,
  VendorConfidentialChip,
  vendorSourceKind,
} from '@/components/ui/citation-chip';
import { Spinner } from '@/components/ui/spinner';
import { WorkspaceButton, useToast } from '@/components/ui/workspace';
import {
  errorMessage,
  useAddCitation,
  useConfirmItems,
  useItemAction,
  useKbItem,
  type KbItemDetail,
} from '@/lib/api/vendor-knowledge';
import {
  Actions,
  Chip,
  FieldHelp,
  FieldLabel,
  FormError,
  InlineForm,
  Linkish,
  MonoNo,
  Quote,
} from '../kb-bits';
import { formatDate, plural, skipReasonText } from '../kb-logic';
import { PassagePicker, type PickedPassage } from '../capture/capture-view';
import { ReasonForm } from '../review/flags';
import { TextArea } from '../review/review-bits';
import { useKb } from '../vendor-knowledge-screen';

/**
 * One item, opened in place (prototype `itemBody()`; E13-F02 §3
 * `GET /items/{id}`): the quote, the citation exactly as the server renders
 * it for this reader, further citations, the library requirement of an
 * answer, its history, and for curators the decisions that keep the item
 * honest: correct the statement in place (the architect's decision), add a
 * citation, replace it with a new numbered item, retire it, keep a flagged
 * one. The quote is the source's own words: it has no edit control.
 */
/**
 * "Used in N future-state maps" (T-0208, prototype `kb-used`): a count from
 * the server, never which engagements or maps (they belong to engagements).
 */
export function UsedInMaps({ count }: { count: number }) {
  return (
    <div
      data-testid="kb-used-in-maps"
      className="mt-3 flex items-center gap-1.5 text-meta text-ink-500"
    >
      <GitBranch className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
      {count > 0
        ? `Used in ${plural(count, 'future-state map')}`
        : 'Not used in a future-state map yet'}
    </div>
  );
}

export function ItemDetail({ itemId }: { itemId: string }) {
  const detail = useKbItem(itemId);
  if (detail.isPending) {
    return (
      <div className="bg-paper-50 px-4 py-4 sm:pl-10">
        <Spinner />
      </div>
    );
  }
  if (detail.isError) {
    return (
      <div className="bg-paper-50 px-4 py-4 text-meta text-posture-critical sm:pl-10">
        {errorMessage(detail.error, 'This item could not be read.')}
      </div>
    );
  }
  return <ItemBody data={detail.data} />;
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4 text-[11px] font-medium uppercase leading-[18px] tracking-[0.06em] text-ink-500">
      {children}
    </div>
  );
}

function verifiedLine(data: KbItemDetail) {
  const parts = data.citation.parts;
  const kind = vendorSourceKind(parts);
  if (kind === 'avero_kb') {
    return (
      <div className="mt-1.5 flex items-center gap-[5px] text-meta text-ink-500">
        <BookOpen className="h-3 w-3" aria-hidden="true" /> As imported from Caliber v1
      </div>
    );
  }
  return (
    <div className="mt-1.5 flex items-center gap-[5px] text-meta text-posture-healthy">
      <Check className="h-3 w-3" aria-hidden="true" />
      {kind === 'web_page'
        ? `Quote found on the captured page, ${parts.date}`
        : `Quote found in the document${parts.locator_label ? `, ${parts.locator_label}` : ''}`}
    </div>
  );
}

const FLAG_WORDS: Record<string, string> = {
  source_changed: 'its page changed and no longer has the words',
  source_unavailable: 'its page is gone',
  not_found_on_source: 'the vendor’s page no longer says it',
  client_name: 'a protected client name was found in it',
};

/** The item's history, oldest first, from the server's own records. */
function historyOf(data: KbItemDetail): Array<{ at: string; text: React.ReactNode }> {
  const i = data.item;
  const out: Array<{ at: string; text: React.ReactNode }> = [];
  if (i.origin === 'v1_import' && i.created) {
    out.push({
      at: i.created.at,
      text: `Imported from Caliber v1 on ${formatDate(i.created.at)}.`,
    });
  } else if (i.origin === 'curator' && i.created) {
    out.push({
      at: i.created.at,
      text: `Added by ${i.created.by?.name ?? 'a curator'} on ${formatDate(i.created.at)}.`,
    });
  } else if (i.origin === 'ai' && i.created) {
    out.push({ at: i.created.at, text: `Proposed by AI on ${formatDate(i.created.at)}.` });
  }
  if (i.confirmed) {
    out.push({
      at: i.confirmed.at,
      text: `Confirmed${i.confirmed.by ? ` by ${i.confirmed.by.name}` : ''} on ${formatDate(i.confirmed.at)}.`,
    });
  }
  if (i.redaction_reviewed) {
    out.push({
      at: i.redaction_reviewed.at,
      text: `Client name removal checked by ${i.redaction_reviewed.by?.name ?? 'a curator'} on ${formatDate(i.redaction_reviewed.at)}.`,
    });
  }
  for (const h of data.history) {
    out.push({
      at: '',
      text:
        h.relation === 'supersedes'
          ? `Replaces ${h.number ?? 'an earlier item'}.`
          : `Replaced by ${h.number ?? 'a new item'}${h.status === 'proposed' ? ', waiting to be confirmed' : ''}.`,
    });
  }
  for (const c of data.corrections) {
    out.push({
      at: c.at,
      text: `Statement corrected${c.by ? ` by ${c.by.name}` : ''} on ${formatDate(c.at)}.`,
    });
  }
  for (const f of data.further_citations) {
    if (f.decided && f.status === 'confirmed') {
      out.push({
        at: f.decided.at,
        text: `Further citation from ${f.citation.parts.source_label}, confirmed${f.decided.by ? ` by ${f.decided.by.name}` : ''} on ${formatDate(f.decided.at)}.`,
      });
    }
  }
  if (data.flag?.flagged_at) {
    out.push({
      at: data.flag.flagged_at,
      text: `Flagged on ${formatDate(data.flag.flagged_at)}: ${FLAG_WORDS[data.flag.reason] ?? data.flag.reason}.`,
    });
  }
  if (i.retired) {
    out.push({
      at: i.retired.at,
      text: `Retired${i.retired.by ? ` by ${i.retired.by.name}` : ''} on ${formatDate(i.retired.at)}${i.retired.reason ? `: ${i.retired.reason}` : ''}.`,
    });
  }
  return out.sort((a, b) => (a.at && b.at ? a.at.localeCompare(b.at) : 0));
}

type Open = 'edit' | 'retire' | 'keep' | 'cite' | 'replace' | null;

function ItemBody({ data }: { data: KbItemDetail }) {
  const { canCurate } = useKb();
  const [open, setOpen] = React.useState<Open>(null);
  const [earlier, setEarlier] = React.useState(false);
  const item = data.item;
  const live = item.status === 'confirmed' || item.status === 'flagged';
  const confidential = data.citation.parts.confidential;
  const history = historyOf(data);
  const further = data.further_citations.filter((f) => f.status !== 'declined');

  return (
    <div className="bg-paper-50 px-4 pb-[18px] pt-1 sm:pl-10 sm:pr-4">
      {item.excerpt_text !== null ? <Quote text={item.excerpt_text} variant="lg" /> : null}
      {verifiedLine(data)}
      {data.requirement ? (
        <>
          <Label>Requirement</Label>
          <div className="mt-1 flex flex-wrap items-baseline gap-2 text-body-sm text-ink-900">
            {data.requirement.library_item_number ? (
              <MonoNo>{data.requirement.library_item_number}</MonoNo>
            ) : null}
            <span className="min-w-0 flex-1">{data.requirement.library_text}</span>
            {data.requirement.answer_label ? (
              <Chip tone="neutral">{data.requirement.answer_label}</Chip>
            ) : null}
          </div>
        </>
      ) : null}
      <Label>Citation, as rendered</Label>
      <VendorCitation citation={data.citation} />
      {confidential ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-meta text-ink-500">
          <VendorConfidentialChip />
          <span>Client pages and exports cite it by title and date only, without the quote.</span>
        </div>
      ) : null}
      {further.length > 0 ? (
        <>
          <Label>Also cited by</Label>
          {further.map((f) => (
            <div key={f.id}>
              <VendorCitation citation={f.citation} />
              {f.status !== 'confirmed' ? (
                <div className="mt-1 text-meta text-ink-500">Offered, waiting under To review</div>
              ) : null}
            </div>
          ))}
        </>
      ) : null}
      <Label>History</Label>
      <ul className="m-0 mt-1.5 flex list-none flex-col gap-2 p-0">
        {history.map((h, k) => (
          <li
            key={k}
            className="relative pl-3.5 text-meta text-ink-700 before:absolute before:left-0 before:top-[7px] before:h-[5px] before:w-[5px] before:rounded-full before:bg-ink-300"
          >
            {h.text}
          </li>
        ))}
      </ul>
      {data.future_maps !== undefined ? <UsedInMaps count={data.future_maps} /> : null}
      {data.corrections.length > 0 ? (
        <div className="mt-2">
          <Linkish tone="quiet" aria-expanded={earlier} onClick={() => setEarlier((e) => !e)}>
            {earlier ? 'Hide earlier wording' : 'Show earlier wording'}
          </Linkish>
          {earlier ? (
            <div className="mt-1.5 border-l-2 border-ink-200 py-1 pl-3 text-body-sm text-ink-500">
              {[...data.corrections].reverse().map((c, k) => (
                <div key={k}>
                  Until {formatDate(c.at)}: {c.previous_statement}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {canCurate && live ? <CuratorActions data={data} open={open} setOpen={setOpen} /> : null}
    </div>
  );
}

function CuratorActions({
  data,
  open,
  setOpen,
}: {
  data: KbItemDetail;
  open: Open;
  setOpen: (o: Open) => void;
}) {
  const { toast } = useToast();
  const action = useItemAction();
  const cite = useAddCitation();
  const confirm = useConfirmItems();
  const item = data.item;
  const label = item.number ?? 'This item';
  const [error, setError] = React.useState<string | null>(null);
  const [statement, setStatement] = React.useState(item.statement ?? '');
  const [passage, setPassage] = React.useState<PickedPassage | null>(null);

  const close = () => {
    setOpen(null);
    setError(null);
    setPassage(null);
  };
  const flagged = item.status === 'flagged';

  const correct = () => {
    setError(null);
    action.mutate(
      { id: item.id, verb: 'correct-statement', body: { statement: statement.trim() } },
      {
        onSuccess: (out) => {
          const removed = typeof out['names_removed'] === 'number' ? out['names_removed'] : 0;
          toast({
            message:
              removed > 0
                ? `Statement corrected. It named a client, and the name was removed.`
                : `Statement corrected. ${label} keeps its number, quote and citations, and maps that cite it aren’t alerted.`,
          });
          close();
        },
        onError: (err) => setError(errorMessage(err)),
      },
    );
  };

  const addCitation = (p: PickedPassage) => {
    setError(null);
    cite.mutate(
      {
        itemId: item.id,
        body: {
          source_version_id: p.sourceVersionId,
          segment_id: p.segmentId,
          start: p.start,
          end: p.end,
        },
      },
      {
        onSuccess: (out) => {
          toast({
            message:
              out['status'] === 'confirmed'
                ? `Added a further citation to ${label}. The item stays as it is.`
                : 'The citation named a client, so it waits under To review.',
          });
          close();
        },
        onError: (err) => setError(errorMessage(err)),
      },
    );
  };

  const replace = async () => {
    if (!passage) return;
    setError(null);
    try {
      const made = (await action.mutateAsync({
        id: item.id,
        verb: 'supersede',
        body: {
          source_version_id: passage.sourceVersionId,
          segment_id: passage.segmentId,
          start: passage.start,
          end: passage.end,
          statement: statement.trim(),
        },
      })) as { id?: string };
      if (!made.id) return;
      const reply = await confirm.mutateAsync({ ids: [made.id], acknowledge: [made.id] });
      const done = reply.confirmed[0];
      const skip = reply.skipped[0];
      toast({
        message: done
          ? `${done.number} replaces ${label}. Both are kept.`
          : `The replacement waits under To review: ${skip ? skipReasonText(skip.reason) : 'it needs a look'}.`,
      });
      close();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  if (open === 'edit') {
    return (
      <InlineForm aria-label="Correct the statement">
        <FieldLabel htmlFor={`edit-${item.id}`}>Statement</FieldLabel>
        <TextArea
          id={`edit-${item.id}`}
          autoFocus
          value={statement}
          onChange={(e) => setStatement(e.target.value)}
        />
        {error ? <FormError>{error}</FormError> : null}
        <FieldHelp>
          Corrects the statement in place. {label}, its quote and its citations stay as they are,
          and maps that cite it aren’t alerted. A number in the statement must appear in the quote.
        </FieldHelp>
        <Actions>
          <WorkspaceButton
            variant="primary"
            disabled={!statement.trim() || action.isPending}
            onClick={correct}
          >
            Save correction
          </WorkspaceButton>
          <WorkspaceButton variant="tertiary" onClick={close}>
            Cancel
          </WorkspaceButton>
        </Actions>
      </InlineForm>
    );
  }
  if (open === 'retire' || open === 'keep') {
    return (
      <ReasonForm
        label={open === 'retire' ? `Why retire ${label}?` : `Why keep ${label} as it is?`}
        placeholder={
          open === 'retire'
            ? 'For example: no longer offered'
            : 'For example: the page still says this elsewhere'
        }
        help={
          open === 'retire'
            ? 'A retired item stays on record and is no longer offered for maps.'
            : 'The flag is cleared and the note is recorded with the item.'
        }
        button={open === 'retire' ? 'Retire' : 'Keep'}
        destructive={open === 'retire'}
        pending={action.isPending}
        error={error}
        onCancel={close}
        onSubmit={(text) =>
          action.mutate(
            {
              id: item.id,
              verb: open === 'retire' ? 'retire' : 'clear-flag',
              body: open === 'retire' ? { reason: text } : { note: text },
            },
            {
              onSuccess: () => {
                toast({
                  message:
                    open === 'retire'
                      ? `Retired ${label}. It stays on record.`
                      : `Kept ${label}. Its flag is cleared.`,
                });
                close();
              },
              onError: (err) => setError(errorMessage(err)),
            },
          )
        }
      />
    );
  }
  if (open === 'cite' || open === 'replace') {
    return (
      <InlineForm aria-label={open === 'cite' ? 'Add a citation' : 'Replace with a new passage'}>
        <div className="font-semibold text-ink-900">
          {open === 'cite'
            ? `Add a further citation to ${label}`
            : `Replace ${label} with a new passage`}
        </div>
        <div className="text-meta text-ink-500">
          {open === 'cite'
            ? 'Another passage that says the same thing. The item stays as it is.'
            : 'A new numbered item quotes the passage you pick and replaces this one, which stays on record.'}
        </div>
        {open === 'replace' && passage ? (
          <>
            <Quote text={passage.text} />
            <div className="mt-3">
              <FieldLabel htmlFor={`rep-${item.id}`}>Statement for the new item</FieldLabel>
              <TextArea
                id={`rep-${item.id}`}
                value={statement}
                onChange={(e) => setStatement(e.target.value)}
              />
            </div>
            {error ? <FormError>{error}</FormError> : null}
            <Actions>
              <WorkspaceButton
                variant="primary"
                disabled={!statement.trim() || action.isPending || confirm.isPending}
                onClick={() => void replace()}
              >
                Replace
              </WorkspaceButton>
              <WorkspaceButton variant="tertiary" onClick={() => setPassage(null)}>
                Pick another passage
              </WorkspaceButton>
              <WorkspaceButton variant="tertiary" onClick={close}>
                Cancel
              </WorkspaceButton>
            </Actions>
          </>
        ) : (
          <>
            <PassagePicker
              vendorId={item.vendor.id}
              actionLabel={open === 'cite' ? 'Add as a citation' : 'Use this passage'}
              onPick={(p) => (open === 'cite' ? addCitation(p) : setPassage(p))}
            />
            {error ? <FormError>{error}</FormError> : null}
            <Actions>
              <WorkspaceButton variant="tertiary" onClick={close}>
                Cancel
              </WorkspaceButton>
            </Actions>
          </>
        )}
      </InlineForm>
    );
  }
  return (
    <Actions>
      <WorkspaceButton onClick={() => setOpen('edit')}>
        <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> Edit statement
      </WorkspaceButton>
      <WorkspaceButton variant="tertiary" onClick={() => setOpen('cite')}>
        Add a citation
      </WorkspaceButton>
      <WorkspaceButton variant="tertiary" onClick={() => setOpen('replace')}>
        Replace
      </WorkspaceButton>
      {flagged && data.flag?.reason !== 'client_name' ? (
        <WorkspaceButton variant="tertiary" onClick={() => setOpen('keep')}>
          Clear flag
        </WorkspaceButton>
      ) : null}
      <WorkspaceButton variant="tertiary" onClick={() => setOpen('retire')}>
        Retire
      </WorkspaceButton>
      <span className="ml-auto flex items-center gap-1 text-meta text-ink-500">
        <ShieldCheck className="h-3 w-3" aria-hidden="true" /> The quote is the source’s own words
        and can’t be edited.
      </span>
    </Actions>
  );
}
